import { useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  StripeError,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent
} from '@stripe/stripe-js';
import { useCallback, useEffect, useState } from 'react';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { addAndSelectCardFromDigitalWallet, createStripeSetupIntent } from '../../../services';
import type { DigitalWallet } from '../../../services/purchase-payments-api.types';

const supportCheckTimeoutMs = 10000;

interface DisplayError {
  headline: string;
  message: string;
}

const digitalWalletErrors = {
  default: {
    headline: 'Card error',
    message: 'Verify your card information is correct and try again.'
  },
  prepaidCard: {
    headline: 'Card error',
    message: "We don't accept prepaid cards. Please use a credit or debit card and try again."
  }
} as const satisfies Record<string, DisplayError>;

const getErrorFromStripeErrorCode = (stripeErrorCode: string | undefined) => {
  switch (stripeErrorCode) {
    case 'incorrect_zip':
      return {
        headline: 'Incorrect ZIP code',
        message: 'Verify your ZIP code is correct and try again.'
      };
    case 'incorrect_number':
      return {
        headline: 'Incorrect card number',
        message: 'Verify your card number is correct and try again.'
      };
    case 'invalid_expiry_year':
    case 'invalid_expiry_month':
      return {
        headline: 'Incorrect expiration date',
        message: 'Verify your expiration date is correct and try again.'
      };
    case 'incorrect_cvc':
      return {
        headline: 'Incorrect CVC',
        message: 'Verify your CVC is correct and try again.'
      };
    case 'setup_intent_authentication_failure':
      return {
        headline: 'Multi-factor authentication failure',
        message: 'We were unable to authenticate your card.'
      };
    default:
      return digitalWalletErrors.default;
  }
};

const getCustomerError = (customerErrorMessage: string | undefined) => ({
  headline: 'Card error',
  message: customerErrorMessage ?? digitalWalletErrors.default.message
});

interface UseDigitalWalletsParams {
  digitalWallet: DigitalWallet | undefined;
  onSupportChange: (value: boolean) => void;
  onCardAdded: () => Promise<{ isCardEligible: boolean }>;
  onSetupConfirmed: () => Promise<boolean>;
  onError: () => void;
}

export const useDigitalWallets = ({
  digitalWallet,
  onSupportChange,
  onCardAdded,
  onSetupConfirmed,
  onError
}: UseDigitalWalletsParams) => {
  const stripe = useStripe();
  const elements = useElements();
  const { trackEvent } = useAnalyticsContext();

  const [isReady, setIsReady] = useState(false);
  const [isSupported, setIsSupported] = useState<boolean | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<DisplayError | null>(null);

  const trackDigitalWalletsEvent = useCallback(
    (eventName: string, eventProperties?: Record<string, unknown>) => {
      trackEvent(`pp_complete_purchase_digital_wallet_${eventName}`, eventProperties);
    },
    [trackEvent]
  );

  useEffect(() => {
    if (isReady) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsReady(true);
      setIsSupported(false);

      trackDigitalWalletsEvent('support_check_timeout', {
        digitalWallet,
        timeoutMs: supportCheckTimeoutMs
      });
    }, supportCheckTimeoutMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [digitalWallet, isReady, trackDigitalWalletsEvent]);

  useEffect(() => {
    if (isSupported !== undefined) {
      onSupportChange(isSupported);
    }
  }, [isSupported, onSupportChange]);

  const saveDigitalWallet = async (): Promise<
    { error?: DisplayError; isCardIneligible?: boolean; didNavigate?: boolean } | undefined
  > => {
    if (!stripe || !elements) {
      throw new Error('Stripe is not initialized');
    }

    const { clientSecret, setupIntentId } = await createStripeSetupIntent();

    if (!clientSecret || !setupIntentId) {
      throw new Error('Failed to create Stripe setup intent');
    }

    const { setupIntent, error: stripeError } = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams: {
        return_url: window.location.href,
        expand: ['payment_method']
      },
      redirect: 'if_required'
    });

    // The backend uses the setup intent to determine the final add-card error state, including Stripe failures.
    const addCardResponse = await addAndSelectCardFromDigitalWallet({ setupIntentId });
    const onCardAddedResponse = await onCardAdded();

    if (!onCardAddedResponse.isCardEligible) {
      return { isCardIneligible: true } as const;
    }

    if (stripeError?.code) {
      trackDigitalWalletsEvent('stripe_error', {
        stripeErrorCode: stripeError.code,
        stripeDeclineCode: stripeError.decline_code
      });

      return { error: getErrorFromStripeErrorCode(stripeError.code) } as const;
    }

    if (addCardResponse.hasError) {
      const isPrepaidCard =
        typeof setupIntent?.payment_method === 'object' &&
        setupIntent?.payment_method?.card?.funding === 'prepaid';

      if (isPrepaidCard) {
        return { error: digitalWalletErrors.prepaidCard } as const;
      }

      return { error: getCustomerError(addCardResponse.customerErrorMessage) } as const;
    }

    const didNavigate = await onSetupConfirmed();

    return { didNavigate } as const;
  };

  const onReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    const availablePaymentMethods = event.availablePaymentMethods;
    const isApplePaySupported = availablePaymentMethods?.applePay ?? false;
    const isGooglePaySupported = availablePaymentMethods?.googlePay ?? false;

    const isSelectedWalletSupported =
      (digitalWallet === 'ApplePay' && isApplePaySupported) ||
      (digitalWallet === 'GooglePay' && isGooglePaySupported) ||
      (digitalWallet === undefined && (isApplePaySupported || isGooglePaySupported));

    if (!isReady) {
      setIsReady(true);
      setIsSupported(isSelectedWalletSupported);
    }

    trackDigitalWalletsEvent('load', {
      digitalWallet,
      availablePaymentMethods,
      isApplePaySupported,
      isGooglePaySupported,
      isSelectedWalletSupported
    });
  };

  const onLoadError = (event: { error: StripeError }) => {
    if (!isReady) {
      setIsReady(true);
      setIsSupported(false);
    }

    trackDigitalWalletsEvent('load_error', {
      digitalWallet,
      stripeError: JSON.stringify(event.error)
    });
  };

  const onClick = (event: StripeExpressCheckoutElementClickEvent) => {
    event.resolve();
    setError(null);

    trackDigitalWalletsEvent('click', {
      digitalWallet,
      walletType: event.expressPaymentType
    });
  };

  const onCancel = () => {
    trackDigitalWalletsEvent('cancel', { digitalWallet });
  };

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    setError(null);

    trackDigitalWalletsEvent('confirm', {
      digitalWallet,
      walletType: event.expressPaymentType
    });

    setIsSaving(true);

    try {
      const saveDigitalWalletResult = await saveDigitalWallet();

      if (saveDigitalWalletResult?.didNavigate) {
        return;
      }

      if (saveDigitalWalletResult?.error) {
        setError(saveDigitalWalletResult.error);
      }

      setIsSaving(false);
    } catch {
      setIsSaving(false);
      onError();
    }
  };

  return {
    isReady,
    isSupported,
    isSaving,
    error,
    onReady,
    onLoadError,
    onClick,
    onCancel,
    onConfirm
  };
};
